import { Formik } from 'formik';
import React from 'react';
import { Container, Row } from 'react-grid-system';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import Button from '../../components/Button';
import FlashMessage from '../../components/FlashMessage';
import FieldWithLabel from '../../components/FormFields/FieldWithLabel';
import Modal from '../../components/Modal';
import Typeahead from '../../components/Typeahead';
import { Header3 } from '../../components/Typography';
import { createLoadingSelector } from '../../redux/loading/loading.selector';
import {
  SEARCH_HUBSPOT_COMPANIES_STORE_KEY,
  searchHubspotCompaniesRequested,
  UPDATE_ORGANIZATION_HUBSPOT_ID_MODAL_STORE_KEY,
  UPDATE_ORGANIZATION_HUBSPOT_ID_STORE_KEY,
  updateOrganizationHubspotIdRequested,
} from '../../redux/organization/organization.actions';
import { getHubspotCompanySearchResults } from '../../redux/organization/organization.selectors';
import { FLASH_MESSAGE_TIMEOUT, useFlashMessage } from '../FlashMessage';

interface EditHubspotIdModalProps {
  organizationId: string;
  hubspotId?: string | null;
  onDismiss: () => void;
}

interface EditHubspotIdFormValues {
  hubspotCompanySearchId: string;
}

const Actions = styled(Row)`
  display: flex;
  justify-content: flex-end;
  column-gap: 8px;
  padding-top: 24px;
`;

const EditHubspotIdModal: React.FC<EditHubspotIdModalProps> = ({ hubspotId, organizationId, onDismiss }) => {
  const dispatch = useDispatch();
  const isSubmitting = useSelector(createLoadingSelector([UPDATE_ORGANIZATION_HUBSPOT_ID_STORE_KEY]));
  const isSearchingHubspotCompanies = useSelector(createLoadingSelector([SEARCH_HUBSPOT_COMPANIES_STORE_KEY]));
  const hubspotCompanySearchResults = useSelector(getHubspotCompanySearchResults);
  const [flashMessage, onDismissFlashMessage] = useFlashMessage([UPDATE_ORGANIZATION_HUBSPOT_ID_MODAL_STORE_KEY]);
  const hubspotCompanySearchItems = hubspotCompanySearchResults.map((company) => ({
    ...company,
    name: `${company.name} (${company.id})${company.domain ? ` - ${company.domain}` : ''}`,
  }));
  const handleDismiss = () => {
    onDismissFlashMessage();
    onDismiss();
  };
  const handleHubspotCompanySearch = (query: string) => {
    const trimmedQuery = query.trim();
    dispatch(searchHubspotCompaniesRequested({ query: trimmedQuery }));
  };
  const handleClearHubspotId = () => {
    onDismissFlashMessage();
    dispatch(updateOrganizationHubspotIdRequested({ organizationId, hubspotId: null }));
  };

  return (
    <Modal onDismiss={handleDismiss}>
      {flashMessage ? (
        <FlashMessage payload={flashMessage} onDismiss={onDismissFlashMessage} timeRemaining={FLASH_MESSAGE_TIMEOUT} />
      ) : null}
      <Header3 style={{ marginBottom: 15 }}>Edit HubSpot ID</Header3>
      <Formik<EditHubspotIdFormValues>
        initialValues={{ hubspotCompanySearchId: '' }}
        onSubmit={(values) => {
          const selectedHubspotCompany = hubspotCompanySearchItems.find(
            (company) => company.id === values.hubspotCompanySearchId,
          );
          if (!selectedHubspotCompany) return;

          onDismissFlashMessage();
          dispatch(updateOrganizationHubspotIdRequested({ organizationId, hubspotId: selectedHubspotCompany.id }));
        }}
      >
        {({ handleSubmit, setFieldValue, values }) => {
          const selectedHubspotCompany = hubspotCompanySearchItems.find(
            (company) => company.id === values.hubspotCompanySearchId,
          );

          return (
            <form onSubmit={handleSubmit}>
              <FieldWithLabel label="HubSpot company" error={undefined} touched={false}>
                <Typeahead
                  emptyValue=""
                  name="hubspotCompanySearchId"
                  options={hubspotCompanySearchItems}
                  selectedOption={selectedHubspotCompany}
                  placeholder="Search by company name"
                  value={values.hubspotCompanySearchId}
                  isLoading={isSearchingHubspotCompanies}
                  onChange={(companyId) => {
                    setFieldValue('hubspotCompanySearchId', companyId);
                  }}
                  onSearch={(query) => {
                    setFieldValue('hubspotCompanySearchId', '');
                    handleHubspotCompanySearch(query);
                  }}
                />
              </FieldWithLabel>
              <Container fluid>
                <Actions>
                  {hubspotId ? (
                    <Button
                      buttonType="warning"
                      disabled={isSubmitting}
                      noArrow={true}
                      onClick={handleClearHubspotId}
                      type="button"
                    >
                      Clear HubSpot ID
                    </Button>
                  ) : null}
                  <Button buttonType="secondary" noArrow={true} onClick={handleDismiss} type="button">
                    Cancel
                  </Button>
                  <Button disabled={isSubmitting || !selectedHubspotCompany} type="submit">
                    Save
                  </Button>
                </Actions>
              </Container>
            </form>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default EditHubspotIdModal;
